import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { sessionParams } from '@playbooks/api';
import { useStorage, useStore, useToast } from '@playbooks/contexts';
import { useLocalStorage, useQuery } from '@playbooks/hooks';
import { AUTH_COOKIE_NAME, extractRawToken } from '@playbooks/utils/auth';
import { logger } from '@playbooks/utils/logger';

import * as LocalStorage from 'local-storage';

type SessionContextProps = {
	user?: any;
	loading: boolean;
	isAuthenticated: () => boolean;
	rootApi: string;
	rootLink: string;
	onAuth: any;
	onUpdate: any;
	onRefresh: any;
	onLogout: any;
	loaded?: any;
};

const storageKey = process.env.NEXT_PUBLIC_STORAGE_KEY;

const SessionContext = React.createContext<SessionContextProps>(null);

const SessionProvider = ({ ssr, children }) => {
	const [user, setUser] = useState<any>(ssr?.session?.data || {});
	const [loaded, setLoaded] = useState(false);
	const router = useRouter();
	const storage = useStorage();
	const store = useStore();
	const toast = useToast();

	// Computed
	const rootApi = `/session`;
	const rootLink = '/settings/account/profile';
	const isAuthenticated = () => (user.id ? true : false);

	// Hooks
	useEffect(() => {
		if (ssr?.session?.data) {
			setUser(ssr?.session?.data);
			storage.storeValues({ session: ssr?.session?.data });
		} else {
			setUser({});
			storage.onClear();
		}
		setLoaded(true);
	}, [storage.loaded, ssr?.session]);

	useEffect(() => {
		if (user) logger.debug('sessionContext: ', user);
	}, [user]);

	useLocalStorage(
		e => {
			if (!loaded) return;
			if (e.key && e.key.includes(`${storageKey}.session`)) {
				const session = LocalStorage.get(`${storageKey}.session`) as any;
				if (session?.id && session.id !== user.id) return setUser(session);
				if (!session?.id && user.id) return onLogout();
			}
		},
		[user.id, loaded],
	);

	// Actions
	const [fetchData, loading] = useQuery(async () => {
		try {
			const response = await store.queryRecord({ url: `/session`, params: sessionParams });
			storage.storeValues({ session: response.data });
			setUser(response.data);
		} catch (e) {
			onLogout();
		}
	});

	// Methods
	const onAuth = async user => {
		const authorization = extractRawToken(user);
		const headers = { Authorization: authorization };
		const response = await store.queryRecord({ url: `/session`, headers, params: sessionParams });
		storage.storeValues({ type: 'User', session: response.data });
		storage.storeCookie(AUTH_COOKIE_NAME, authorization, true);
		setUser(response.data);
		return response.data;
	};

	const onUpdate = data => {
		setUser({ ...user, ...data });
	};

	const onRefresh = () => fetchData();

	const onLogout = () => {
		setUser({});
		storage.onClear();
		toast.showSuccess(200, `You've been logged out.`);
		router.push('/');
	};

	// Render
	return (
		<SessionContext.Provider
			value={{
				user,
				loading,
				isAuthenticated,
				rootApi,
				rootLink,
				onAuth,
				onUpdate,
				onRefresh,
				onLogout,
				loaded,
			}}>
			{children}
		</SessionContext.Provider>
	);
};

const useSession = () => {
	return React.useContext(SessionContext);
};

export { SessionProvider, useSession };

// Docs
// https://www.npmjs.com/package/local-storage
